(function () {
  'use strict';

  class EcoDomusAvailability extends HTMLElement {
    connectedCallback() {
      if (this._domusInitialised) return;
      this._domusInitialised = true;

      this.form = this.closest('form');
      this.titleElement = this.querySelector('[data-domus-availability-title]');
      this.detailElement = this.querySelector('[data-domus-availability-detail]');
      this.variantData = this.readVariantData();
      this.currentState = 'unknown';
      this.updateFrame = null;

      if (!this.form) return;

      this.handleVariantEvent = () => this.queueUpdate();
      this.handleFormInput = (event) => {
        if (event.target?.matches?.('[name="quantity"], [name="id"]')) this.queueUpdate();
      };
      this.handleSubmit = (event) => {
        if (this.currentState === 'discontinued') {
          event.preventDefault();
          this.getPurchaseElements().addButton?.focus();
        }
      };

      this.form.addEventListener('variant:change', this.handleVariantEvent);
      this.form.addEventListener('variant:changed', this.handleVariantEvent);
      this.form.addEventListener('product:rerender', this.handleVariantEvent);
      this.form.addEventListener('change', this.handleFormInput);
      this.form.addEventListener('input', this.handleFormInput);
      this.form.addEventListener('submit', this.handleSubmit);

      this.queueUpdate();
    }

    disconnectedCallback() {
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      if (!this.form) return;
      this.form.removeEventListener('variant:change', this.handleVariantEvent);
      this.form.removeEventListener('variant:changed', this.handleVariantEvent);
      this.form.removeEventListener('product:rerender', this.handleVariantEvent);
      this.form.removeEventListener('change', this.handleFormInput);
      this.form.removeEventListener('input', this.handleFormInput);
      this.form.removeEventListener('submit', this.handleSubmit);
    }

    queueUpdate() {
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);

      // Warehouse replaces the payment container after its variant events fire.
      // Waiting until the next animation frame ensures we update the new button,
      // rather than a button that Shopify is about to remove from the page.
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    readVariantData() {
      try {
        return JSON.parse(this.querySelector('[data-domus-variant-data]')?.textContent || '{}');
      } catch (error) {
        console.warn('Eco Smart: Domus variant availability data could not be read.', error);
        return {};
      }
    }

    getSelectedVariantId() {
      return String(this.form?.elements?.id?.value || '');
    }

    getQuantityInput() {
      return this.form?.querySelector('[name="quantity"]') || null;
    }

    getRequestedQuantity() {
      const value = Number.parseInt(this.getQuantityInput()?.value || '1', 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    rememberOriginalMaximum(input) {
      if (!input || Object.prototype.hasOwnProperty.call(input.dataset, 'domusOriginalMax')) return;
      input.dataset.domusOriginalMax = input.getAttribute('max') || '';
    }

    restoreOriginalMaximum(input) {
      if (!input) return;
      this.rememberOriginalMaximum(input);
      const originalMaximum = input.dataset.domusOriginalMax;
      if (originalMaximum) input.setAttribute('max', originalMaximum);
      else input.removeAttribute('max');
    }

    applyDiscontinuedMaximum(input, stock) {
      if (!input || !Number.isFinite(stock) || stock < 1) return;
      this.rememberOriginalMaximum(input);

      const originalMaximum = Number.parseInt(input.dataset.domusOriginalMax || '', 10);
      const maximum = Number.isFinite(originalMaximum) ? Math.min(originalMaximum, stock) : stock;
      input.setAttribute('max', String(maximum));

      const requested = Number.parseInt(input.value || '1', 10);
      if (Number.isFinite(requested) && requested > maximum) input.value = String(maximum);
    }

    getPurchaseElements() {
      const addButton = this.form?.querySelector('.product-form__payment-container .product-form__add-button');
      return {
        addButton,
        addButtonWrapper: addButton?.closest('buy-button') || addButton,
        paymentButton: this.form?.querySelector('.shopify-payment-button')
      };
    }

    setPurchaseState(state) {
      const { addButton, addButtonWrapper, paymentButton } = this.getPurchaseElements();
      this.currentState = state;

      if (addButtonWrapper) addButtonWrapper.hidden = false;

      if (state === 'discontinued') {
        if (addButton) {
          addButton.type = 'button';
          addButton.disabled = true;
          addButton.setAttribute('disabled', 'disabled');
          addButton.setAttribute('aria-disabled', 'true');
          addButton.classList.add('button--disabled');
          addButton.classList.remove('button--primary');
          addButton.textContent = 'Sold out';
        }
        if (paymentButton) paymentButton.hidden = true;
        return;
      }

      if (addButton) {
        addButton.type = 'submit';
        addButton.disabled = false;
        addButton.removeAttribute('disabled');
        addButton.removeAttribute('aria-disabled');
        addButton.classList.remove('button--disabled');
        addButton.classList.add('button--primary');
        addButton.textContent = state === 'backorder' ? 'Backorder' : 'Add to cart';
      }

      if (paymentButton) paymentButton.hidden = state !== 'in-stock';
    }

    showMessage(title, detail, state) {
      this.hidden = false;
      this.dataset.domusState = state;
      if (this.titleElement) this.titleElement.textContent = title;
      if (this.detailElement) this.detailElement.textContent = detail;
      this.setPurchaseState(state);
    }

    hideMessage() {
      this.hidden = true;
      this.dataset.domusState = 'unknown';
      this.currentState = 'unknown';
    }

    update() {
      const variant = this.variantData[this.getSelectedVariantId()];
      const stockRaw = String(variant?.stock ?? '').trim();
      const discontinued = variant?.discontinued === true;
      const quantityInput = this.getQuantityInput();

      if (!stockRaw) {
        this.restoreOriginalMaximum(quantityInput);
        this.hideMessage();
        return;
      }

      const openEndedStock = stockRaw === '100+';
      const stock = openEndedStock ? Number.POSITIVE_INFINITY : Number.parseInt(stockRaw, 10);

      if (!openEndedStock && !Number.isFinite(stock)) {
        this.restoreOriginalMaximum(quantityInput);
        this.hideMessage();
        return;
      }

      if (discontinued && stock <= 0) {
        this.restoreOriginalMaximum(quantityInput);
        this.showMessage(
          'Discontinued',
          'Contact us for a suitable alternative: (02) 9517 2701.',
          'discontinued'
        );
        return;
      }

      if (discontinued && !openEndedStock) {
        this.applyDiscontinuedMaximum(quantityInput, stock);
      } else {
        this.restoreOriginalMaximum(quantityInput);
      }

      const requestedQuantity = this.getRequestedQuantity();
      const shouldBackorder = !discontinued && !openEndedStock && (
        stock <= 0 || requestedQuantity > stock
      );

      if (shouldBackorder) {
        this.showMessage(
          'Available to backorder',
          'Place your order now and we will confirm the estimated delivery date.',
          'backorder'
        );
        return;
      }

      const displayedQuantity = openEndedStock || stock >= 50 ? '50+' : String(stock);
      this.showMessage(
        `In stock — ${displayedQuantity} available`,
        'Dispatch within 1–3 business days.',
        'in-stock'
      );
    }
  }

  if (!window.customElements.get('eco-domus-availability')) {
    window.customElements.define('eco-domus-availability', EcoDomusAvailability);
  }
})();
